/**
 * Asset-free alarm tone on the Web Audio API — ported from cpanel
 * `model/alarmSound.ts`. A repeating two-tone klaxon sweep that loops while
 * armed. Browsers keep an AudioContext suspended until a user gesture, so on a
 * page nobody has clicked yet the siren stays silent (the visual alarm and the
 * toast still fire) until the next interaction.
 */

/** Sweep shape: 660 Hz → 1320 Hz → 660 Hz over 0.7 s, re-fired every 850 ms. */
const SWEEP_LOW_HZ = 660;
const SWEEP_HIGH_HZ = 1320;
const SWEEP_PEAK_S = 0.35;
const SWEEP_END_S = 0.7;
const SWEEP_INTERVAL_MS = 850;
const GAIN = 0.35;

export class AlarmSound {
  private ctx: AudioContext | null = null;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private running = false;

  private getContext(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    // `webkitAudioContext` is the pre-standard Safari name; read it through a
    // structural lookup rather than widening `window`'s type.
    const legacy: unknown = Reflect.get(window, 'webkitAudioContext');
    const Ctor =
      typeof window.AudioContext === 'function'
        ? window.AudioContext
        : typeof legacy === 'function'
          ? (legacy as typeof AudioContext)
          : undefined;
    if (!Ctor) return null;
    this.ctx ??= new Ctor();
    return this.ctx;
  }

  /** One siren sweep: rises then falls, with a soft attack and release. */
  private playSweep(): void {
    const ctx = this.getContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(SWEEP_LOW_HZ, now);
    osc.frequency.linearRampToValueAtTime(SWEEP_HIGH_HZ, now + SWEEP_PEAK_S);
    osc.frequency.linearRampToValueAtTime(SWEEP_LOW_HZ, now + SWEEP_END_S);

    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(GAIN, now + 0.05);
    gain.gain.setValueAtTime(GAIN, now + 0.6);
    gain.gain.linearRampToValueAtTime(0, now + 0.72);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.75);
  }

  /** Arm the siren: resume the context and loop the sweep until stopped. */
  start(): void {
    if (this.running) return;
    this.running = true;
    // Resume rejects before any user gesture — harmless while suspended.
    void this.getContext()
      ?.resume()
      .catch(() => undefined);
    this.playSweep();
    this.intervalId = setInterval(() => this.playSweep(), SWEEP_INTERVAL_MS);
  }

  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.running = false;
  }

  /** Release the audio context entirely (on unmount). */
  dispose(): void {
    this.stop();
    void this.ctx?.close().catch(() => undefined);
    this.ctx = null;
  }
}
